import React from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Select as MUISelect
} from '@mui/material';

import * as R from 'ramda';

import { makeStyles } from 'tss-react/mui';

import { colors } from '@constants/colors';
import { fonts } from '@constants/fonts';
import { SAFE_ZINDEX } from '@constants/mui-theme';

import { FormHelperText, Progress } from '@mui';

import { FormControl } from '../formControl/FormControl';
import { InputLabel } from '../inputLabel/InputLabel';
import { MenuItem } from './MenuItem';

// tss-react only supports one function for the whole styles object (theme, params), not a
// function per-rule like @mui/styles allowed — these were inlined into that single function
// below (using params.floatingLabelFixed) instead of being called per-rule.
const useStyles = makeStyles()((theme, { floatingLabelFixed = false }) => ({
  inputRoot: {
    fontSize: fonts.md
  },
  // v5 shrank the input element's own padding/height (4px 0 5px / 1.4375em) from v4's
  // (6px 0 7px / 1.1876em) — restored here to keep every field's height/spacing unchanged.
  // lineHeight is set explicitly (not just inherited from root) because root's own font-size
  // is forced to 14px by inputRoot above, while the input's real font-size is 16px (set via
  // the theme's MuiInputBase.input override in root.js) — v5's own line-height is in `em`,
  // so left alone it resolves against root's 14px, not the input's real 16px, landing a few
  // px too tall. Explicit here so it resolves against the input's own, correct font-size.
  inputInput: {
    padding: '6px 0 7px',
    height: '1.1876em',
    lineHeight: '1.1876em',
    // v5's own Select styles add a `minHeight: '1.4375em'` (plus `height: 'auto'`) using a
    // higher-specificity `&.MuiSelect-select` selector specifically to win over the input
    // base's own height — v4 had no such rule. That beats the height/lineHeight above, so
    // the doubled class selector here matches its specificity to win the tie instead.
    '&&': {
      height: '1.1876em',
      minHeight: '1.1876em'
    }
  },
  inputUnderline: {
    '&:before': {
      borderBottom: `1px solid ${colors.neutral.silver}`
    },
    '&:after': {
      borderBottom: `1px solid ${colors.neutral.silver}`
    }
  },
  inputLabel: floatingLabelFixed
    ? { position: 'unset', color: colors.neutral.gray }
    : {
      color: colors.neutral.gray,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      bottom: '0px',
      maxWidth: '100%'
    },
  inputLabelShrunk: floatingLabelFixed
    ? { transform: 'unset', fontSize: fonts.sm }
    : {
      fontSize: fonts.sm,
      overflow: 'unset',
      textOverflow: 'unset',
      whiteSpace: 'unset'
    },
  inputLabelFocused: {
    color: `${colors.neutral.gray} !important`
  },
  inputLabelError: {
    color: `${theme.palette.error.main} !important`
  },
  formControl: {
    'label + &': {
      marginTop: '0'
    }
  },
  menuPaper: {
    maxHeight: '25rem'
  }
}));

export const Select = props => {
  const { classes } = useStyles(R.omit(['classes'], props));

  const {
    label,
    style,
    helperText,
    multiple,
    input,
    disabled,
    loading = false,
    error = false,
    fullWidth = true,
    floatingLabelFixed = false,
    InputLabelProps = {},
    InputProps = {},
    FormControlProps = {},
    MenuProps = {},
    variant = 'standard',
    ...data
  } = props;

  const select = (
    <MUISelect
      {...data}
      multiple={multiple}
      disabled={disabled || loading}
      IconComponent={
        loading
          ? iconProps => <Progress {...iconProps} size={16} />
          : props.IconComponent
      }
      MenuProps={{
        ...MenuProps,
        anchorOrigin: {
          horizontal: 'left',
          vertical: 'bottom'
        },
        // Without an explicit transformOrigin, v5's Popover defaults its horizontal value to
        // 'center' — combined with anchorOrigin's 'left' above, that shifts the menu left by
        // half its own width instead of aligning its left edge with the field's left edge
        // (visible as menus opening far to the left of, instead of directly below, the field).
        transformOrigin: {
          horizontal: 'left',
          vertical: 'top'
        },
        classes: {
          paper: classes.menuPaper,
          ...MenuProps?.classes
        },
        style: SAFE_ZINDEX
      }}
      input={
        input || (
          <Input
            {...InputProps}
            classes={{
              root: classes.inputRoot,
              input: classes.inputInput,
              underline: classes.inputUnderline,
              ...(floatingLabelFixed && { formControl: classes.formControl }),
              ...InputProps?.classes
            }}
          />
        )
      }
    />
  );

  if (!label) {
    return React.cloneElement(select, { fullWidth, style });
  }

  return (
    <FormControl fullWidth={fullWidth} error={error} style={style} variant={variant} {...FormControlProps}>
      <InputLabel
        {...InputLabelProps}
        variant={variant}
        {...(floatingLabelFixed && {
          classes: {
            root: classes.inputLabel,
            shrink: classes.inputLabelShrunk,
            focused: classes.inputLabelFocused,
            error: classes.inputLabelError,
            ...InputLabelProps?.classes
          },
          shrink: true
        })}
      >
        {label}
      </InputLabel>
      {select}
      {helperText && (
        <FormHelperText {...props.FormHelperTextProps}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

Select.MenuItem = MenuItem;

Select.propTypes = {
  FormControlProps: PropTypes.object,
  FormHelperTextProps: PropTypes.object,
  IconComponent: PropTypes.object,
  InputLabelProps: PropTypes.object,
  InputProps: PropTypes.object,
  MenuProps: PropTypes.object,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  floatingLabelFixed: PropTypes.bool,
  fullWidth: PropTypes.bool,
  helperText: PropTypes.string,
  input: PropTypes.node,
  label: PropTypes.string,
  loading: PropTypes.bool,
  multiple: PropTypes.bool,
  style: PropTypes.object,
  variant: PropTypes.string
};

export default Select;
